/**
 * Copyright (c) Microsoft. All rights reserved.
 * Type definitions for Trouter JavaScript Client 2022.35.01.29
 */

// tslint:disable:interface-name


/**
 * Trouter HTTP-like headers
 */
export type Headers = {
    [name: string]: string;
};

/**
 * Trouter HTTP-like request (service -> Trouter -> app)
 *
 * Represents one incoming request received by Trouter Client. Its content is
 * mostly a verbatim copy of what was sent by the originating service to
 * Trouter Service.
 */
export interface Request {
    readonly id: number;        /// Request/response ID, useful for
                                /// logging/debugging/tracing purposes
    readonly method: string;    /// HTTP method (GET, POST, ...)
    readonly path: string;      /// HTTP path, including listener's prefix and
                                /// any provided query parameters
    readonly headers: Headers;  /// HTTP headers
    readonly body: string;      /// HTTP body/content
}

/**
 * Possible return values of Response.send()
 */
export const enum SendResponseResult {
    OK           = 0,  /// Response accepted, will be sent back to the service
    Timeout      = 1,  /// Too late, 504 (Timeout) has been already sent back
    Duplicate    = 2,  /// send() was called multiple times on one Response
    Incomplete   = 3,  /// Mandatory parameters (status code) are not set
    Disconnected = 4   /// Trouter connection dropped, cannot reply now
}

/**
 * Trouter HTTP-like response (app -> Trouter -> service)
 *
 * Represents one outgoing response to be sent by Trouter Client. Always set
 * the status code, add any extra headers or body, then call `send()`.
 */
export interface Response {
    readonly id: number;  /// Request/response ID, useful for
                          /// logging/debugging/tracing purposes
    status: number;       /// HTTP status code (200, 404, ...), mandatory
    headers: Headers;     /// HTTP headers, optional
    body: string;         /// HTTP body/content, optional

    /**
     * Send the response
     *
     * Trouter will add just a few of its own headers and otherwise forward the
     * response to the originating service that had sent the request.
     *
     * A successful return value `OK` means only that the response message has
     * been accepted and prepared for sending back. The actual transmission will
     * happen asynchronously later and might still fail for various reasons.
     *
     * @return SendResponseResult.OK if successful, other code if not
     */
    send(): SendResponseResult;
}

/**
 * Information about the current Trouter connection
 *
 * Received in `Listener.onTrouterConnected()` every time the connection
 * parameters change.
 */
export interface ConnectionInfo {
    readonly baseEndpointUrl: string;   /// Base URL used for routing messages
                                        /// from services to this client
                                        /// instance, i.e. not specific to
                                        /// a particular listener
    readonly newEndpointUrl: boolean;   /// Tells if this `baseEndpointUrl` is
                                        /// different from the previous one and
                                        /// therefore if any dependent service
                                        /// registrations need to be updated to
                                        /// know where to send data
    readonly c2cUrlBase: string;        /// Base URL prefix used for routing
                                        /// messages from clients to this client
                                        /// - replace the URL base (protocol +
                                        /// hostname) of `baseEndpointUrl` (or
                                        /// another per-listener endpoint URL)
                                        /// with this value to obtain the full
                                        /// client-to-client URL
    readonly clientId: string;          /// ID of this Trouter client, should
                                        /// stay the same between reconnects
    readonly connectionId: string;      /// ID of the current Trouter
                                        /// connection, can change between
                                        /// reconnects
    readonly connectionTtlSec: number;  /// Expected lifetime of the current
                                        /// connection (ID) in seconds, as
                                        /// determined by Trouter Service
}

/**
 * Trouter listener
 *
 * Interface implemented by application to receive Trouter callbacks
 */
export interface Listener {
    /**
     * Called when Trouter connection is (re-)established
     *
     * This is important mostly because of the `endpointUrl` as the URL where
     * messages are sent to by the originating service(s). Only after receiving
     * this callback and learning the Endpoint URL can the application register
     * the established channel for receiving messages.
     *
     * The Endpoint URL is intended for service-to-service communication, only
     * whitelisted authenticated services can access it and initiate Trouter
     * requests. Use `c2cUrlBase` from `connectionInfo` to obtain a similar URL
     * for client-to-client communication - see also `replaceTrouterUrlBase()`.
     *
     * @param endpointUrl    A URL used for routing messages from services to
     *                       this listener
     * @param connectionInfo Additional Trouter connection information
     */
    onTrouterConnected(endpointUrl: string, connectionInfo: ConnectionInfo): void;

    /**
     * Called when Trouter connection drops
     *
     * No incoming requests can be received for now, no outgoing responses can
     * be sent either. Trouter client will reconnect as soon as possible.
     *
     * This state is most commonly caused by some temporary network issues
     * (switching from Wi-Fi to a wired network, bad signal etc.) or things
     * like a sleep/resume transition on the device.
     *
     * @note This method does not need to be implemented.
     */
    onTrouterDisconnected?(): void;

    /**
     * Called when Trouter receives a request intended for this listener
     *
     * Application can inspect the contents of the incoming message by looking
     * at `request`, then fill in the outgoing `reponse` and send it back by
     * invoking its method `send()`.
     *
     * This can be done both synchronously when this method is called, or at a
     * later time asynchronously from any other context, if the application
     * stores the `response` object aside.
     *
     * In any case, a response ought to be sent to every received request. If
     * it is not, Trouter client will send a 504 (Timeout) message itself after
     * a configured time, but that will needlessly show up as an error on the
     * service side, even if you have processed the message successfully.
     *
     * For most simple applications, just sending a response with 200 (OK) right
     * away should be good enough.
     *
     * @param request  Incoming request object (read-only)
     * @param response Outgoing response object (to be filled in and sent)
     */
    onTrouterRequest(request: Request, response: Response): void;

    /**
     * Called when some messages have been dropped on Trouter
     *
     * This can be called at any time by Trouter server
     *
     * Some of the examples of the message loss:
     * The message loss might occur when client queues got full and messages are rejected
     * The message to be delivered are already expired.
     *
     * @param flowTags String array of flows representing the type of messages being lost
     *
     * returns true in case that the flow tags has been processed and the information can be dropped
     */
    onTrouterMessageLoss?(flowTags: string[]): boolean;

    /**
     * Called when the user activity state has been delivered and accepted by the service
     * The value of correlation vector will be extended,
     * and so only the prefix passed in should be compared, if necessary.
     *
     * @param cv Correlation vector of user activity state update.
     */
    onTrouterUserActivityStateAccepted?(cv: string): void;
}

/**
 * States that a user can reach
 *
 * Use with the `setUserActivityState()` method.
 */
export enum UserActivityState {
    Unknown  = 0,  /// Initial state
    Active   = 1,  /// User is active according to the application
    Inactive = 2   /// User is not active according to the application
}

/**
 * Trouter client
 *
 * Use to register and unregister your listeners. Calls are forwarded to the
 * underlying implementation, whatever that is on the current platform.
 */
export interface Client {
    /**
     * Register a listener
     *
     * The specified listener will now be notified about any Trouter events. An
     * initial `onTrouterConnected()` callback will be received automatically
     * right away if Trouter is already connected, so that the listener can
     * learn the Endpoint URL etc.
     *
     * There can be only one listener registered for each `path`, but the path
     * can naturally comprise of multiple levels separated with a slash. The
     * listener with the longest, most specific matching path prefix for an
     * incoming message will be invoked to handle it.
     *
     * If a matching listener is found, the incoming message is not passed
     * further to any registered legacy MessageHandler callbacks.
     *
     * This method is very light-weight and can be called at any time.
     *
     * @param listener Listener object to register
     * @param path     HTTP-like relative path to register (e.g. '/foo')
     * @return `true` if successfully registered,
     *         `false` if there already is a registered listener for this exact
     *         path or if the given path is invalid
     */
    registerListener(listener: Listener, path: string): boolean;

    /**
     * Unregister a listener
     *
     * The specified listener will not be notified about any Trouter events
     * anymore. `onTrouterDisconnected()` will not be called automatically
     * during unregistration.
     *
     * All registrations of this `Listener` object (i.e. possibly more than one,
     * registered with different paths) will be unregistered.
     *
     * This method is very light-weight and can be called at any time.
     *
     * @param listener Listener object to unregister
     * @return `true` if successfully unregistered,
     *         `false` if not registered in the first place
     */
    unregisterListener(listener: Listener): boolean;

    /**
     * Set user activity state
     *
     * Sets the state of the user activity that is then sent to trouter if it
     * is different from the previous state (including Unknown), or upon
     * reconnect, or under other circumstances fully in the discretion of the
     * client. Can throw an instance of Error if that's not possible.
     *
     * @param state The new state of user activity
     * @param correlationVector Correlation vector for end to end tracking
     */
    setUserActivityState(state: UserActivityState, correlationVector?: string): void;
}


// tslint:disable:no-any

export type LogProvider = {
    log(...message: any[]): void;
    warn(...message: any[]): void;
    error(...message: any[]): void;
    debug(...message: any[]): void;
    info(...message: any[]): void;
};

/** Headers for HTTP request */
export class HttpHeaders {
    [name: string]: string;
}

export type TrouterMessage = {
    eventId: number | null;
    url?: string;
    headers?: HttpHeaders;
    body?: TrouterRequestBody;
    rawBody?: string;
};

export type TrouterRequestBody = {
    evt?: number;
    eventId?: number;
    gp?: string;
    nsp?: string;
};


export interface MessageHandler {
    handleMessage(message: TrouterMessage): HandleMessageResult | undefined;
}

export interface HandleMessageResult {
    /**
     * @deprecated, return 'undefined' from MessageHandler.handleMessage
     * if you want to indicate the message was not handled
     */
    isHandled?: boolean;

    resultCode: number;
    responseBody?: string;
    responseHeaders?: HttpHeaders;
}

export enum TrouterState {
    /** Initial state */
    Unknown = 0,

    /** ready to work */
    Connected = 2,

    /** waiting for 'connected' event */
    Disconnected = 3,

    /** waiting for 'register' from second connection */
    Switching = 9
}

export interface TelemetryEvent {
    name: string;
    properties: {
        [name: string]: {
            value: string | number | boolean | undefined;
        }
    };
}

export interface ITelemetrySender {
    logEvent(clientEvent: TelemetryEvent): void;
}

/**
 * Called once token is required.
 * Trouter can cache token - it's defined by config option in TrouterSettings
 */
export type SkypeTokenProvider = (forceRefresh: boolean) => Promise<string>;

/**
 * Stores timeout values to simplify switch between in-call and normal modes
 */
export interface TimeoutOptions {
    /** Timeout for connection to trouter service, i.e. for whole allocation + socket.io connection (all transports together) */
    connectionTimeoutMs: number;

    /** Timeout for fetch requests, i.e. for allocation, health, registration, unregistration */
    fetchTimeoutMs: number;

    /** Interval between ping requests */
    pingTimeoutMs: number;

    /** Timeout for waiting for pong response (websockets) */
    pongTimeoutMs: number;

    /** Maximal value for backoff timeout */
    maxBackoffMs: number;

    /** Timeout for handling of an incoming request before a 504 response is sent back automatically */
    requestTimeoutMs: number;

    /** For how long to wait for response to activity update event. */
    userActivityResponseTimeoutMs?: number;
}

export interface TrouterSettings {
    /**
     * GO allocation url of trouter for V4 - like https://go.trouter.teams.microsoft.com/v4/a
     */
    trouterServiceUrl: string;
    version: string;
    productName: string;
    /**
     * aka Client Correlation ID - for correlation between Trouter and other components. It makes sense to persist it if possible
     */
    sessionId: string;
    /**
     * Environment for telemetry, e.g. "latest", "liveint", "dogfood", "prod"
     */
    environment: string;

    /**
     * full URL of EDF registrar service (like https://prod.registrar.skype.com/v2/registrations), if left undefined then Trouter does not
     * register its connection and other registration params can be left undefined as well
     */
    registrarServiceUrl?: string;
    /** mandatory parameter if registration is required */
    registrationId?: string;
    /**
     * @deprecated and will be removed in next versions. Use maxRegistrationTimeInMs instead
     */
    registrarRefreshTimeoutInMs?: number;
    /** mandatory parameter if registration is required */
    pnhAppId?: string;
    /** mandatory parameter if registration is required */
    pnhTemplate?: string;
    /** optional product context to set with the registration */
    pnhProductContext?: string;
    /** optional context to set with the Trouter transport registration */
    pnhContext?: string;
    /** mandatory parameter if registration is required */
    platform?: string;
    /** mandatory parameter if registration is required */
    platformUIVersion?: string;
    /**
     * @deprecated and will be removed in next versions. Use maxRegistrationTimeInMs instead
     */
    registrarTtlInSeconds?: number;
    /** optional, if set - limits registration TTL in EDF. If not - value from Trouter server is used */
    maxRegistrationTimeInMs?: number;

    /** Timeouts for normal mode */
    timeoutOptions?: TimeoutOptions;
    /**
     * For historical reasons it exists here and in 'timeoutOptions', but if 'timeoutOptions' is
     * specified - this value will be ignored
     */
    trouterConnectTimeoutInMs?: number;

    /** Timeout for being in 'in-call' mode, i.e. with aggressive ping/retry policy. If it's zero/undefined - in-call mode is disabled */
    incallModeTimeoutMs?: number;
    /** Timeouts for in-call mode, i.e. with more aggressive timeouts/reconnect policy */
    incallTimeoutOptions?: TimeoutOptions;

    /** Flag to disable internal token cache and always call provided SkypeTokenProvider */
    disableInternalSkypeTokenCache?: boolean;

    /** After how long to send the user activity second time after reconnect. */
    userActivitySecondResendDelayMs?: number;

    retryLimitOnTokenFetch?: number;
}

export interface TrouterTelemetrySettings {
    enabled: boolean;

    trouter_js_client_connected?: boolean;
    trouter_js_client_disconnected?: boolean;
    trouter_js_client_error?: boolean;
    trouter_js_client_progress?: boolean;
    trouter_js_client_response?: boolean;
    trouter_js_client_request?: boolean;
    trouter_js_client_registration?: boolean;
    trouter_js_client_unregistration?: boolean;
    trouter_js_client_check_connection?: boolean;

    /** Send trouter_js_client_progress once this number of steps is accumulated */
    numberOfStepsToMaintain?: number;
    /** Send trouter_js_client_progress with current steps after a given timeout */
    sendProgressTimeoutSecs?: number;

    /**
     * true if health requests should be sent in telemetry events
     */
    logHealthCheckError?: boolean;
    /**
     * True if ping errors should be sent in telemetry events
     */
    logSendPingError?: boolean;
    /**
     * @deprecated and will be removed in next versions
     */
    maxBackoffInMs?: number;
}

export interface TrouterTelemetryConfig {
    eventLogger: ITelemetrySender;
    settings: TrouterTelemetrySettings;
}

/**
 * Connection parameters cache
 *
 * Implement to allow saving connection parameters across sessions (e.g. in
 * window.localStorage) and thus retaining the same Trouter URL longer.
 */
export interface IConnectionCache {
    /** Called when the library is asking the host app for any cached connection data */
    onGetTrouterConnectionCache(): Promise<string | undefined>;
    /** Called when the library has updated data to be externally cached */
    onSetTrouterConnectionCache(content: string): void;
}

export interface ITrouterServiceConfig {
    trouterSettings: TrouterSettings;
    skypeTokenProvider: SkypeTokenProvider;
    telemetryConfig: TrouterTelemetryConfig;
    connectionCache?: IConnectionCache;
    registrationStateCallbackForAcsDoNotUse?(isRegistered: boolean): void;
}

/**
 * Information about trouter connection
 */
export interface IConnectionInfo {
    /** Secure Trouter url at which client can be reached */
    url: string;

    /** Return connection TTL, i.e. number of seconds till expiration */
    getRemainingTtlInSec(): number;
}
export type StateChangedListener = (state: TrouterState, url: string) => void;

export type TrouterConnectionListener = (state: TrouterState, info?: IConnectionInfo) => void;

export interface ITrouterServiceBase extends Client {
    start(options: ITrouterServiceConfig): void;
    stop(dontUnregister?: boolean): void;

    setEcsConfig(fullEcsData: {}): Promise<void>;

    /**
     * Can be called in two cases:
     * - either network is down (disconnectDetected === true) - trouter will verify that
     *      connection is really down and will start to restoring it;
     * - network is restored (disconnectDetected === false) - trouter will try to restore connection immediately;
     */
    checkConnection(disconnectDetected?: boolean): void;

    /**
     * Resends endpoint registration to Registrar if registration is being
     * handled by the Trouter Client internally.
     * Can throw an instance of Error if that's not possible.
     */
    resendRegistration(): Promise<void>;

    // Everything below is legacy API, deprecated, do not use
    registerMessageHandler(handler: MessageHandler): void;
    clearMessageHandlers(): void;
    state(): TrouterState;
    getTrouterUrlAsync(): Promise<string>;
    onStateChanged(callback: StateChangedListener | undefined): void;
    offStateChanged(callback: StateChangedListener): boolean;
    addCallback(callback: TrouterConnectionListener): void;
    removeCallback(callback: TrouterConnectionListener): boolean;
}

/**
 * Creates instance of trouter implementation
 */
export declare function createTrouterService(logger: LogProvider): ITrouterServiceBase;

/**
 * Creates instance of trouter implementation
 */
export declare function getTrouterServiceVersion(): string;

/**
 * Helper function to replace URL base (protocol + hostname)
 *
 * Useful when converting Trouter Endpoint URL to its client-to-client variant
 * with `ConnectionInfo.c2cUrlBase`.
 */
export declare function replaceTrouterUrlBase(existingUrl: string, newBase: string): string;



// WEBPACK FOOTER //
// ./lib_temp/Interfaces.ts